import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["StepViewComponent", "showProgressIndicator", "showLogo", "showAccountLink"];
/* hs-eslint ignored failing-rules */

import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getFullUrl } from 'hubspot-url-utils';
import { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutTemplate } from 'signup-components/components/LayoutTemplate/LayoutTemplate';
import { LayoutType } from 'signup-components/types';
import { Step } from 'signup-constants/Step';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import styled from 'styled-components';
import { LoadingKey } from '../app-active-loading/LoadingKey';
import { useChatBot } from '../chatbot/useChatBot';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { ACQ0143TitleContainer } from '../experiments/acq0143/ACQ0143TitleContainer';
import { getTotalSteps } from '../experiments/acq0143/ACQ143Utils';
import { isInACQ142VariantAMSS } from '../experiments/gswhV1/gswhUtils';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { getCurrentStepNumbering } from '../flow-management/stepNumberingSelectors';
import { useCanGoBack } from '../hooks/useCanGoBack';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { getLoginUrl } from '../http-login/getLoginUrl';
import imageUrls from '../images/imageUrls';
import { useMessage } from '../messaging/useMessage';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getHaveAnAccountMessage } from '../step-landing-page/HaveAnAccountLink';
import { getLoginRedirectUrl } from '../step-landing-page/getLoginRedirectUrl';
import { goPrevStep } from '../store/flow/action-creators';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { getActiveLoading } from '../store/loading-status/selectors';
import { useTracker } from '../tracking/useTracker';
import { getStepConfig } from '../views/getStepConfig';
import { AppFooterComponent } from './AppFooterComponent';
import { AppMessagingComponent } from './AppMessagingComponent';
import { getIsAuthenticated } from '../store/auth/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MessagingAboveStepContentContainer = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__MessagingAboveStepContentContainer",
  componentId: "sc-2v1f13-0"
})(["display:flex;justify-content:center;"]);
const StyledBackgroundImage = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__StyledBackgroundImage",
  componentId: "sc-2v1f13-1"
})(["background:url(", ") no-repeat;position:fixed;z-index:-2147483647;display:block;width:100%;height:100%;background-size:100% 100%;transform:scale(1.01);"], imageUrls.productBackground);
export const LayoutTemplateViewContainer = _ref => {
  let {
      StepViewComponent,
      showProgressIndicator = true,
      showLogo = true,
      showAccountLink = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isActive: isInstanceOfSignupEmbedder,
    isEmbedded
  } = useSignupEmbedder();
  useChatBot();
  const isAuthed = useSelector(getIsAuthenticated);
  const {
    currentStep
  } = useCurrentStep();
  const loginRedirectUrl = getLoginRedirectUrl();
  const {
    canGoBack,
    completedStep
  } = useCanGoBack();
  const dispatch = useDispatch();
  const {
    currentStep: currentStepNumber
  } = useSelector(getCurrentStepNumbering);
  const activeLoading = useSelector(getActiveLoading);
  const {
    component
  } = useMessage();
  const Tracker = useTracker();
  const {
    isMobile
  } = useResponsiveness();
  const {
    url,
    altText,
    width: logoWidth,
    height: logoHeight
  } = getFlowConfig().getAppLogo(isMobile);
  const stepConfig = getStepConfig(currentStep);
  const showMessagingAboveContent = stepConfig && stepConfig.showMessagingAboveContent;
  const {
    layoutType,
    waveType,
    shouldShowProgressBar,
    decorativeBgComponent
  } = useLayoutTemplate();
  const alignCentredStepAtTheTop = isEmbedded && !activeLoading || stepConfig && stepConfig.alignCentredStepAtTheTop && !activeLoading;
  const {
    isAssignedToSpecificVariant: isAssignedToSpecificVariantACQ142
  } = useExperiment(ExperimentKeys.ACQ0142, TrackWhen.never);
  const isACQ142VariantAMSS = useSelector(isInACQ142VariantAMSS);
  const {
    isAssignedToVariant: isACQ0143Variant
  } = useExperiment(ExperimentKeys.ACQ0143, TrackWhen.never);
  const postAccountCreationSteps = useSelector(getPostAccountCreationSteps);
  const shouldShowBackLink = Boolean(canGoBack && completedStep);
  const onBackLinkClick = event => {
    event.preventDefault();
    Tracker.backButton(currentStep, completedStep);
    dispatch(goPrevStep());
  };
  const onAccountLinkClick = () => {
    Tracker.signupInteraction('click-login-have-an-account', {}, true);
    Tracker.redirect({
      url: `${getFullUrl('app')}/login`
    });
  };
  const onLogoClick = () => {
    Tracker.signupInteraction('click-hubspot-company-logo', {
      currentStep
    }, true);
  };
  const renderMessagingAboveStepContent = () => {
    if (showMessagingAboveContent && !isACQ0143Variant()) {
      return /*#__PURE__*/_jsx(MessagingAboveStepContentContainer, {
        children: /*#__PURE__*/_jsx(AppMessagingComponent, {})
      });
    }
    return null;
  };
  const shouldShowAccountLinkByStep = isAssignedToSpecificVariantACQ142(ExperimentParameterGroup.Variant_B) ? currentStep === Step.ProductExperience : currentStep === Step.LandingPage;
  const shouldShowHaveAnAccountLink = showAccountLink ? shouldShowAccountLinkByStep && !isAuthed : false;
  const isACQ142MSSPostAccCreation = postAccountCreationSteps.includes(currentStep) && isACQ142VariantAMSS && !isMobile && activeLoading !== LoadingKey.ACQ142SignupRequest;
  const shouldShowLogo = () => {
    if (isACQ142MSSPostAccCreation || activeLoading === LoadingKey.ACQ142LoginAfterSignupMS) {
      return false;
    }
    return showLogo;
  };
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldShowManageCookiesLink = !isInstanceOfSignupEmbedder && !isPostAccountCreationStep && !activeLoading;
  window.onclick = event => {
    if (isACQ142MSSPostAccCreation) {
      const targetClass = event.target.className;
      // Check that the click target is outside the content-holder element
      // If it is, we can assume that the user has clicked the product background
      const isProductBackground = ['FlowStepComponent', 'HeaderContainer', 'ViewContainerWrapper'].some(substring => targetClass.includes(substring));
      if (isProductBackground) {
        Tracker.signupInteraction('click-outside-step-content');
      }
    }
  };
  const totalSteps = useSelector(getTotalSteps);
  return /*#__PURE__*/_jsxs("div", Object.assign({}, props, {
    children: [isACQ142VariantAMSS && layoutType === LayoutType.FramedCentredStep && !isMobile && /*#__PURE__*/_jsx(StyledBackgroundImage, {}), /*#__PURE__*/_jsxs(LayoutTemplate, {
      accountLinkHref: getLoginUrl({
        redirectUrl: loginRedirectUrl
      }),
      accountLinkMessage: getHaveAnAccountMessage(),
      alignCentredStepAtTheTop: alignCentredStepAtTheTop,
      currentStepNumber: currentStepNumber,
      decorativeBgComponent: decorativeBgComponent,
      layoutType: layoutType,
      logoAltText: altText,
      logoHeight: logoHeight,
      logoImageSrc: url,
      logoWidth: logoWidth,
      messagingContent: component && component(),
      shouldAddTopPadding: !isEmbedded,
      shouldHalveProgressBar: getFlowConfig().shouldHalveProgressBar,
      showBackLink: shouldShowBackLink,
      showHaveAnAccountLink: shouldShowHaveAnAccountLink,
      showLogo: shouldShowLogo(),
      showManageCookiesLink: shouldShowManageCookiesLink,
      showProgressIndicator: showProgressIndicator && shouldShowProgressBar,
      titleContainer: /*#__PURE__*/_jsx(ACQ0143TitleContainer, {}),
      totalSteps: totalSteps,
      waveType: waveType,
      onAccountLinkClick: onAccountLinkClick,
      onBackLinkClick: onBackLinkClick,
      onLogoClick: onLogoClick,
      children: [renderMessagingAboveStepContent(), /*#__PURE__*/_jsxs(Suspense, {
        fallback: /*#__PURE__*/_jsx(UILoadingSpinner, {
          grow: true,
          size: "extra-small"
        }),
        children: [/*#__PURE__*/_jsx(StepViewComponent, {}), isInstanceOfSignupEmbedder && /*#__PURE__*/_jsx(AppFooterComponent, {})]
      })]
    })]
  }));
};